import React, { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { HelpCircleIcon } from 'lucide-react';

import { queryClient } from 'api';
import { EditableEvent } from 'types/event.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { cn } from '@epoch-ui/lib/utils';
import { Label } from '@epoch-ui/components/Form';
import { Switch } from '@epoch-ui/components/Switch/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@epoch-ui/components/Tooltip';
import { useEditableEvent } from 'pages/dashboard/overview/events/editor/hooks/useEditableEvent';
import { eventSessionsRegistrationLimitApi } from 'pages/dashboard/overview/events/editor/api/eventSessionsRegistrationLimit';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';

interface EditSessionRegistrationLimitToggleProps extends React.HTMLAttributes<HTMLDivElement> {}

const EditSessionRegistrationLimitToggle: React.FC<EditSessionRegistrationLimitToggleProps> = ({
  className,
  ...props
}) => {
  const event = useEditableEvent();
  const { showToast } = useToaster();
  const updateRegistrationLimitMutation = useMutation({
    mutationFn: eventSessionsRegistrationLimitApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
      showToast('Registration limit updated.');
    },
    onError: () => {
      // Invalidate query to rollback optimistic update on failure
      queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
      showToast('Failed to update registration limit.', { variant: ToasterVariant.ERROR });
    }
  });

  /**
   * Used to disable 'Limit registration' toggle.
   * For multi session optionality.
   *
   * Note: This should check if ANY SESSION has confirmed/incomplete status.
   * Check if current check makes sense at parent level or update it.
   *
   * TODO: Move this check to backend
   */
  const disableLimitRegistration = useMemo(() => {
    if (!event?.guestListTotals) return false;

    return (event.guestListTotals.confirmed || 0) + (event.guestListTotals.incomplete || 0) > 0;
  }, [event?.guestListTotals]);

  const handleToggleChange = async (checked: boolean): Promise<void> => {
    await queryClient.cancelQueries({
      queryKey: eventEditorQueryKeys.event(event.id!)
    });
    updateRegistrationLimitMutation.mutate({
      eventId: event.id!,
      limitRegistration: checked
    });
    queryClient.setQueryData(eventEditorQueryKeys.event(event.id!), (prev: EditableEvent) => ({
      ...prev,
      limitRegistration: checked
    }));
  };

  return (
    <div {...props} className={cn('tw-flex tw-justify-center tw-gap-2', className)}>
      <Switch
        id="limit-registration-toggle"
        checked={event.limitRegistration}
        className="tw-peer"
        disabled={
          updateRegistrationLimitMutation.isPending || event.hasEnded || disableLimitRegistration
        }
        onCheckedChange={handleToggleChange}
      />
      <Label
        className="tw-m-0 tw-flex tw-items-center tw-gap-1"
        htmlFor="limit-registration-toggle"
      >
        Limit registration to 1 session{' '}
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircleIcon
              size={14}
              tabIndex={0}
              role="button"
              aria-label="Learn more about limit registration to 1 session"
            />
          </TooltipTrigger>
          <TooltipContent className="tw-max-w-80">
            When guests register for the event, they will only be able to select one session to
            attend. This setting can’t be changed once the event has guests.{' '}
            <a
              target="_blank"
              rel="noreferrer"
              className="tw-text-background tw-underline hover:tw-text-background"
              href="https://help.epochapp.com/articles/how-do-multi-session-events-work"
            >
              Learn more
            </a>
          </TooltipContent>
        </Tooltip>
      </Label>
    </div>
  );
};

export { EditSessionRegistrationLimitToggle };
